/**
 * Authentication layer — thin, honest wrapper around Firebase Auth.
 *
 * No mock users, no fake success states. If Firebase isn't configured yet,
 * every method rejects with a typed error the UI can render as
 * "Sign-in is not available yet" rather than pretending to succeed.
 */
import {
  getAuth,
  onAuthStateChanged as fbOnAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  type User,
  type Auth,
} from 'firebase/auth';
import { getFirebaseApp, isFirebaseConfigured } from './config';

export class AuthNotConfiguredError extends Error {
  constructor() {
    super('Authentication is not configured yet. Firebase credentials are missing.');
    this.name = 'AuthNotConfiguredError';
  }
}

let authInstance: Auth | null = null;

function getAuthInstance(): Auth {
  if (!isFirebaseConfigured) throw new AuthNotConfiguredError();
  if (!authInstance) authInstance = getAuth(getFirebaseApp());
  return authInstance;
}

export function onAuthStateChanged(callback: (user: User | null) => void): () => void {
  if (!isFirebaseConfigured) {
    // No Firebase yet — report "signed out" and do nothing further.
    callback(null);
    return () => {};
  }
  return fbOnAuthStateChanged(getAuthInstance(), callback);
}

export async function signIn(email: string, password: string): Promise<User> {
  const result = await signInWithEmailAndPassword(getAuthInstance(), email, password);
  return result.user;
}

export async function registerAccount(email: string, password: string): Promise<User> {
  const result = await createUserWithEmailAndPassword(getAuthInstance(), email, password);
  return result.user;
}

export async function signOutUser(): Promise<void> {
  await fbSignOut(getAuthInstance());
}

export async function requestPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(getAuthInstance(), email);
}

/** Completes the reset flow using the oobCode Firebase appends to the reset link. */
export async function confirmReset(oobCode: string, newPassword: string): Promise<void> {
  await confirmPasswordReset(getAuthInstance(), oobCode, newPassword);
}

/** Requires recent sign-in; caller must handle `auth/requires-recent-login`. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const auth = getAuthInstance();
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error('No authenticated user.');
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
}
